// Mesajlar › Yeni kişiler — hastaya eşleşmeyen WhatsApp numaraları (whatsapp:contacts).

import React from "react";
import { Avatar, Box, ButtonBase, Skeleton, Typography, alpha } from "@mui/material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import { EmptyState, SearchField, formatPhoneDisplay } from "../../components/atoms";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { listTimeLabel } from "./messageFormat";

export const contactName = (c) => c?.profileName || formatPhoneDisplay(c?.phone) || c?.phone || "Bilinmeyen numara";

export function ContactAvatar({ size = 38 }) {
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: alpha(COLORS.successDark, 0.12), color: COLORS.successDark }}>
      <WhatsAppIcon sx={{ fontSize: Math.round(size * 0.5) }} />
    </Avatar>
  );
}

function UnreadBadge({ count, palette }) {
  if (!count) return null;
  return (
    <Box
      component="span"
      aria-label={`${count} okunmamış mesaj`}
      sx={{
        flexShrink: 0,
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: 10,
        bgcolor: palette.primary,
        color: COLORS.white,
        fontSize: TYPO.caption,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
}

function ContactRow({ contact, selected, onSelect, palette }) {
  const unread = contact.unread || 0;
  const name = contactName(contact);
  const phone = formatPhoneDisplay(contact.phone);
  return (
    <ButtonBase
      onClick={() => onSelect(contact)}
      aria-current={selected ? "true" : undefined}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2, md: 2.5 },
        py: 1.5,
        textAlign: "left",
        borderBottom: `1px solid ${COLORS.slate100}`,
        bgcolor: selected ? alpha(palette.primary, 0.06) : "transparent",
        boxShadow: selected ? `inset 3px 0 0 ${palette.primary}` : "none",
        "&:hover": { bgcolor: selected ? alpha(palette.primary, 0.08) : alpha(palette.primary, 0.03) },
      }}
    >
      <ContactAvatar />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography noWrap sx={{ flex: 1, fontSize: TYPO.body, fontWeight: unread ? 600 : 500, color: COLORS.slate800 }}>
            {name}
          </Typography>
          <Typography sx={{ flexShrink: 0, fontSize: TYPO.caption, color: unread ? palette.primary : COLORS.slate400 }}>
            {listTimeLabel(contact.lastMessageAt)}
          </Typography>
        </Box>
        {contact.profileName && phone ? (
          <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate400, fontVariantNumeric: "tabular-nums" }}>
            {phone}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Typography noWrap sx={{ flex: 1, fontSize: TYPO.label, color: unread ? COLORS.slate700 : COLORS.slate500 }}>
            {contact.lastMessagePreview || ""}
          </Typography>
          <UnreadBadge count={unread} palette={palette} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

function ListSkeleton() {
  return Array.from({ length: 5 }).map((_, i) => (
    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${COLORS.slate100}` }}>
      <Skeleton variant="circular" width={38} height={38} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="50%" height={18} />
        <Skeleton width="80%" height={16} />
      </Box>
    </Box>
  ));
}

/**
 * @param {object[]} contacts
 * @param {boolean} loading
 * @param {string|null} selectedId
 * @param {(c: object) => void} onSelect
 * @param {string} search
 * @param {(v: string) => void} onSearchChange
 * @param {number} total
 * @param {object} palette
 */
export default function ContactList({ contacts, loading, selectedId, onSelect, search, onSearchChange, total, palette }) {
  const q = search.trim();
  const empty = !loading && contacts.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 1.5, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate100}` }}>
        <SearchField
          placeholder="Ad veya numarayla ara"
          value={search}
          onChange={onSearchChange}
          autoComplete="off"
          fullWidth
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
        {loading ? <ListSkeleton /> : null}
        {empty ? (
          q ? (
            <EmptyState
              variant="illustrated"
              icon={<WhatsAppIcon />}
              title="Kişi bulunamadı"
              description={`“${q}” aramanızla eşleşen kişi yok. Aramayı değiştirin veya temizleyin.`}
            />
          ) : (
            <EmptyState
              variant="illustrated"
              icon={<WhatsAppIcon />}
              title="Henüz yeni kişi yok"
              description="Kayıtlı hastalarınızla eşleşmeyen numaralardan WhatsApp'a gelen mesajlar burada görünür."
            />
          )
        ) : null}
        {contacts.map((c) => (
          <ContactRow
            key={c._id}
            contact={c}
            selected={String(c._id) === String(selectedId)}
            onSelect={onSelect}
            palette={palette}
          />
        ))}
        {total > contacts.length ? (
          <Typography sx={{ px: 2.5, py: 1.5, fontSize: TYPO.caption, color: COLORS.slate400, textAlign: "center" }}>
            En son {contacts.length} kişi gösteriliyor; diğerleri için aramayı kullanın.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
